import * as Popover from '@radix-ui/react-popover';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import type { ClassNames, Matcher } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import { ChevronIcon } from '../ChevronIcon/ChevronIcon';
import { dayPickerClassNames } from './DatePicker.styles';

const TODAY = new Date();

TODAY.setHours(0, 0, 0, 0);

const FUTURE_LIMIT = addDays(TODAY, 365 * 10); // 10 years

// For now, we only have variants for the trigger button. We might have variants for
// the DayPicker later, in which case we can define a separate DAY_PICKER_VARIANTS object.
const TRIGGER_BUTTON_VARIANTS = variants({
  base: [
    cn(
      'relative flex items-center gap-sm py-sm pl-md pr-sm',
      'rounded-sm border transition-colors duration-100 border-emphasis-subtle',
      'bg-emphasis-default text-inverse hover:bg-emphasis-strong',
    ),
  ],
  variants: {
    variant: {
      default: [],
      warning: ['border-warning-default'],
      error: ['border-error-default'],
    },
  },
});

export interface DatePickerProps {
  value?: Date;
  onApply: (date: Date) => void;
  minDate?: Date;
  allowFutureDates?: boolean;
  placeholder?: string;
  highlightToday?: boolean;
  variant?: 'default' | 'warning' | 'error';
}

export function DatePicker({
  onApply,
  value,
  minDate,
  allowFutureDates = true,
  placeholder = 'Select Date',
  highlightToday = false,
  variant = 'default',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const handleSelectDate = useCallback(
    (date: Date) => {
      onApply(date);
      setOpen(false);
    },
    [onApply],
  );

  const displayValue = value ? format(value, 'MMM d, yyyy') : placeholder;

  const disabledDates = useMemo(() => {
    const matchers: Matcher[] = [];

    if (minDate) {
      matchers.push({ before: minDate });
    }

    if (!allowFutureDates) {
      matchers.push({ after: TODAY });
    }

    return matchers;
  }, [minDate, allowFutureDates]);

  const classNames: Partial<ClassNames> = { ...dayPickerClassNames };

  if (highlightToday) {
    classNames.today = '[&>button]:underline [&>button]:underline-offset-4';
  }

  const chevronDirection = open ? 'up' : 'down';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="date-picker-trigger"
          aria-invalid={variant === 'error'}
          className={TRIGGER_BUTTON_VARIANTS({ variant })}
        >
          <span className="text-white">{displayValue}</span>
          <ChevronIcon direction={chevronDirection} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          className="z-10 mt-xs w-[18rem] rounded-sm border bg-level-03 border-emphasis-subtle"
        >
          <div className="flex">
            <DayPicker
              showOutsideDays
              required={true}
              mode="single"
              captionLayout="dropdown"
              disabled={disabledDates}
              startMonth={minDate || TODAY}
              endMonth={allowFutureDates ? FUTURE_LIMIT : TODAY}
              selected={value}
              onSelect={handleSelectDate}
              numberOfMonths={1}
              classNames={classNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
              data-testid="date-picker-calendar"
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

DatePicker.displayName = 'DatePicker';
